import { useLocation, useNavigate } from "react-router-dom";
import Innerbanner from "../../components/inner-banner";
import { Link } from "react-router-dom";
import Image from "../../components/ui/image";
import { Images } from "../../lib/images";
import { getClubDetails } from "../../Services/apis";
import { useEffect, useState } from "react";
import Loader from "../../components/loader";

interface Team {
  club_id: string;
  club_name: string;
  country: string;
  score: number;
  logo_url: string;
}

// ----------------------
// SMART NUMBER FORMATTER
// ----------------------
const formatSmartNumber = (value: any) => {
  if (value === null || value === undefined || value === "") return "0";

  const num = Number(value);
  if (isNaN(num)) return value;

  // Integers → comma only
  if (Number.isInteger(num)) {
    return num.toLocaleString("en-US");
  }

  // Decimals → 2 decimals + commas
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// ----------------------
// FINANCIAL VALUE FORMAT
// ----------------------
const formatFinancialValue = (value: any) => {
  if (!value || isNaN(Number(value))) return "£0";

  const num = Number(value);

  if (num >= 1_000_000_000) {
    return `£${(num / 1_000_000_000).toFixed(2)} B`;
  } else if (num >= 1_000_000) {
    return `£${(num / 1_000_000).toFixed(2)} M`;
  } else {
    return `£${num.toLocaleString("en-US")}`;
  }
};

const ClubProfileDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const team = (location.state as { team: Team })?.team;

  const [clubDetails, setClubDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // --- always define functions & hooks BEFORE any conditional return ---
  const getClubPrifileDetails = async () => {
    try {
      if (!team) return; // safe exit
      setLoading(true);
      const response = await getClubDetails(team.club_id);
      setClubDetails(response.data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching club details:", error);
    }
  };

  useEffect(() => {
    if (team) {
      getClubPrifileDetails();
    }
  }, [team]);

  // --- now safe conditional return AFTER hooks ---
  if (!team) {
    return <p className="text-red-500 p-6">No team data available</p>;
  }

  return (
    <div className="w-full">
      {loading ? <Loader /> : null}
      <Innerbanner bnrHeading={team?.club_name} />

      <section className="bg-background p-5">
        <div className="container mx-auto pb-10">
          <div className="col-span-3 md:col-span-1 bg-white rounded-xl p-5 mb-5">
            <button
              onClick={() => navigate(-1)}
              className="text-blue-600 font-medium mb-5"
            >
              ←
            </button>

            {/* Header Section */}
            <div className="block md:flex gap-5 mb-5">
              {/* <div>
                <Image
                  src={team?.logo_url || Images.Image}
                  alt={team?.club_name}
                  className="w-10 h-10 object-contain"
                  width={40}
                  height={40}
                />
              </div> */}
              <div className="flex-1 pb-3">
                <h2 className="text-2xl font-medium">{team?.club_name}</h2>
                <p className="text-gray-600">{team.country}</p>
              </div>
              <div className="flex-1 text-start md:text-end">
                {clubDetails?.website_url ? (
                  <Link
                    to={clubDetails.website_url}
                    target="_blank"
                    className="text-[#0086D6] inline-flex items-center"
                  >
                    <Image
                      alt="pop"
                      src={Images.Linkpop}
                      className="h-5 me-2"
                    />
                    {clubDetails.website_url}
                  </Link>
                ) : (
                  <span className="text-gray-500">No website available</span>
                )}
              </div>
            </div>

            {/* Row 1 */}
            <div className="block md:flex border-t border-gray-300 pt-2 pb-0 lg:pb-2 flex-wrap">
              <Item label="Team Alias" value={clubDetails?.alias} />
              <Item label="Stadium Name" value={clubDetails?.stadium} />
              <Item
                label="Top Flight English Titles"
                value={formatSmartNumber(
                  clubDetails?.trophies?.top_flight_titles,
                )}
              />
              <Item
                label="Top Flight European Titles"
                value={formatSmartNumber(
                  clubDetails?.trophies?.european_titles,
                )}
              />
            </div>

            {/* Row 2 */}
            <div className="block md:flex lg:border-t border-gray-300 pt-2 pb-0 lg:pb-2 flex-wrap">
              <Item
                label="FA Cup Titles"
                value={formatSmartNumber(clubDetails?.trophies?.fa_cup_titles)}
              />
              <Item
                label="League Cup Titles"
                value={formatSmartNumber(
                  clubDetails?.trophies?.league_cup_titles,
                )}
              />
              <Item
                label="Financial Value"
                value={formatFinancialValue(clubDetails?.financial_value)}
              />
              <Item
                label="Average Attendance"
                value={formatSmartNumber(clubDetails?.avg_attendance)}
              />
            </div>

            {/* Row 3 */}
            <div className="block md:flex lg:border-t border-gray-300 pt-2 pb-0 lg:pb-2 flex-wrap">
              <Item
                label="Seasons in Top Division"
                value={formatSmartNumber(clubDetails?.seasons_top_div)}
              />
              <Item
                label="Trophies Won (Last 5 Years)"
                value={formatSmartNumber(clubDetails?.trophies?.last5_trophies)}
              />
              <Item label="Current League" value={clubDetails?.current_league || "N/A"} />
              <Item
                label="League Position"
                value={formatSmartNumber(clubDetails?.league_position)}
              />
            </div>

            {/* Row 4 */}
            <div className="block md:flex lg:border-t border-gray-300 pt-2 pb-0 lg:pb-2 flex-wrap">
              <Item
                label="Avg Position (Last 5 Years)"
                value={formatSmartNumber(clubDetails?.avg_position_5y)}
              />
              <Item label="Region" value={clubDetails?.region} />
              <Item
                label="Facebook Followers"
                value={formatSmartNumber(clubDetails?.followers)}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Reusable Component
const Item = ({ label, value }: { label: string; value: any }) => (
  <div className="flex-none w-[100%] lg:w-[50%] xl:w-[25%] px-3 lg:border-r border-gray-300 border-b lg:border-b-0 mb-2 lg:mb-0">
    <h6 className="text-sm font-normal text-gray-400">{label}</h6>
    <h4 className="text-lg font-normal text-black">{value ?? "0"}</h4>
  </div>
);

export default ClubProfileDetails;
